import useLandingContents from "components/landing/hooks/useLandingContents";
import classNames from "components/ui/classNames";
import Link from "next/link";
import { preserveOnlyUtmParameters } from "lib/analytics/utm";
import { useRouter } from "next/router";
import { FormattedMessage, useIntl } from "react-intl";
import { useBooleanFlag, useFlagValue } from "lib/flagging/FeatureFlags";
import useIsPortuguese from "lib/hooks/useIsPortuguese";
import getBaseUrl from "lib/utils/getBaseUrl";
import CountUp from "react-countup";
import { format } from "util";
import { useAmplitude } from "lib/amplitude/Amplitude";

export default function PlatformMetricsSection() {
  const intl = useIntl();
  const languageCode = useIsPortuguese();
  const { platformMetrics, isUsLanguage } = useLandingContents();
  const displayVideo = useBooleanFlag("marketing/landing/displayVideoOnLanding");

  return (
    <section className="mx-auto flex max-w-7xl flex-col items-center px-6 pb-16 lg:px-0">
      <header className="mx-auto flex max-w-3xl flex-col gap-4">
        <h1 className="text-center text-3xl font-black lg:text-5xl">
          <FormattedMessage defaultMessage="Análise de Ações, Fundos Imobiliários, Stocks e REITs" id="rqcjGp" />
        </h1>

        <p className="text-center text-lg text-zinc-600">
          <FormattedMessage
            defaultMessage="O melhor site de Buy&Hold do Brasil com acesso imediato a mais de 6.000 ativos!"
            id="NMfQgR"
          />
        </p>
      </header>

      {displayVideo && languageCode === "pt-BR" ? (
        <div className="m-4 mx-auto my-12 max-w-6xl overflow-hidden rounded-xl lg:my-16 lg:h-[528px]">
          <div className="aspect-video w-full overflow-hidden rounded-3xl border border-slate-950 bg-slate-800 shadow-sm lg:h-[528px]">
            <iframe
              title={intl.formatMessage({ defaultMessage: "Conheça o Fundamentei", id: "ISuEVu" })}
              className="h-full w-full rounded-4xl"
              allowFullScreen={true}
              src="https://player-vz-62789cee-886.tv.pandavideo.com.br/embed/?v=964ab507-c9a5-43f1-ac3c-c19211ee3354"
              allow="accelerometer;gyroscope;autoplay;encrypted-media;picture-in-picture"
            />
          </div>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl p-4 py-12 lg:h-[628px] lg:py-4">
          <video
            muted={true}
            autoPlay={true}
            loop={true}
            playsInline={true}
            className={classNames(
              "pointer-events-none w-full max-w-6xl overflow-hidden rounded-3xl bg-transparent object-cover",
              "scale-125 md:scale-100 lg:h-[628px]",
            )}
          >
            <source
              type="video/mp4"
              src={format(
                "%s/static/landing/video/%s/fundamentals.mp4",
                getBaseUrl(),
                intl.locale === "pt-BR" ? "pt" : "en",
              )}
            />
          </video>
        </div>
      )}

      <LandingCallToAction />

      <div className="grid items-center gap-12 lg:grid-cols-4 lg:gap-0 lg:divide-x lg:divide-slate-100">
        {(isUsLanguage ? platformMetrics.US : platformMetrics.BR).map(({ indicator, value, ...rest }) => (
          <div key={indicator} className="flex flex-col gap-2 px-5 text-center lg:mt-4">
            <h4 className="text-2xl font-bold lg:text-4xl">
              <CountUp end={value} duration={2.75} enableScrollSpy={false} {...rest} />
            </h4>

            <p className="text-sm text-zinc-500">{indicator}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function LandingCallToAction() {
  const router = useRouter();
  const amplitude = useAmplitude();
  const displayVideo = useFlagValue("marketing/landing/displayVideoOnLanding");

  return (
    <div className="pb-16 lg:px-0">
      <Link
        href={{
          pathname: "/sign-up",
          query: preserveOnlyUtmParameters(router.query),
        }}
        className={classNames(
          "mx-auto flex w-full max-w-xl items-center justify-center rounded-[32px] p-4",
          "text-center text-base font-medium shadow-sm",
          "md:p-6 md:text-xl",
          "tracking-tight hover:rounded-2xl hover:tracking-tighter",
          "border transition-all",
          "border-teal-950 bg-teal-900 text-teal-100 hover:bg-teal-800 hover:text-teal-200",
        )}
        onClick={() => {
          amplitude.logEvent("Clicked on Sign Up on the landing page", {
            content: displayVideo ? "video" : "gif",
            location: "below-content",
          });
        }}
      >
        <FormattedMessage defaultMessage="Clique aqui e cadastre-se em segundos!" id="BcGhZU" />
      </Link>
    </div>
  );
}
